import {
  combinedHipRidgeNote,
  PITCH_OPTIONS,
  ROOF_LAYOUT_FIELDS,
  WASTE_PCT_OPTIONS,
  hasWasteTable,
  parseRoofReport,
  reportNameMismatch,
  type Job,
  type RoofLayout,
} from "@roof-hawk/core";
import { useRef, useState } from "react";

import { Modal } from "../../components/ui";
import { t, tn } from "../../i18n";

type Draft = Record<string, string>;

const emptyDraft = (): Draft =>
  Object.fromEntries(ROOF_LAYOUT_FIELDS.map((f) => [f.key, ""]));

const num = (value: string): number | null => {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

export interface RoofLayoutInput {
  name: string | null;
  average_pitch: string;
  waste_pct: number;
  layers: number;
  source: string;
  pdf_filename: string | null;
  pdfFile: File | null;
  /** A report already stored, when the roof came off the wall card's PDF. */
  pdf_path?: string | null;
  values: Record<string, number | null>;
}

/**
 * Roof Measurement.
 *
 * Two ways in: drop the measuring service's PDF on it and let the numbers be
 * read off, or key them by hand. Both land in the same form — the PDF route
 * fills the boxes and the rep confirms them, because a report that parsed
 * badly must not become a priced roof without anyone looking at it.
 *
 * A job can hold several layouts; the house and the detached garage measure
 * separately and price separately.
 */
export function RoofMeasurementModal({
  job,
  layouts,
  onClose,
  onSave,
  onDelete,
}: {
  job: Job;
  layouts: readonly RoofLayout[];
  onClose: () => void;
  onSave: (input: RoofLayoutInput) => Promise<void>;
  onDelete: (layout: RoofLayout) => Promise<void>;
}) {
  const [mode, setMode] = useState<"choose" | "manual">(
    layouts.length ? "choose" : "manual",
  );

  if (mode === "choose") {
    return (
      <LayoutList
        layouts={layouts}
        onClose={onClose}
        onAdd={() => setMode("manual")}
        onDelete={onDelete}
      />
    );
  }

  return (
    <ManualLayoutForm
      job={job}
      onClose={layouts.length ? () => setMode("choose") : onClose}
      onSave={onSave}
    />
  );
}

function LayoutList({
  layouts,
  onClose,
  onAdd,
  onDelete,
}: {
  layouts: readonly RoofLayout[];
  onClose: () => void;
  onAdd: () => void;
  onDelete: (layout: RoofLayout) => Promise<void>;
}) {
  return (
    <Modal
      title={t("Roof Measurement")}
      subtitle={t("Every layout measured on this job.")}
      onClose={onClose}
      actions={
        <>
          <button className="btn-ghost" onClick={onClose}>
            {t("Close")}
          </button>
          <button className="btn-primary" onClick={onAdd}>
            {t("Add a layout")}
          </button>
        </>
      }
    >
      {/* Its own row rather than the label/value grid the rest of the app
          uses. That grid gave the name a fixed 150px column and ran everything
          else — the squares, the pitch, the waste, "from PDF" and the Remove
          link — together as one paragraph in the second column, which wrapped
          on a phone and buried the link somewhere in the middle of it.
          Jonathon, 2026-08-26: "it was pretty jumbled up and I couldn't see the
          remove button."

          Now the layout gets the width it needs, its numbers read underneath
          it, and Remove is a button pinned to the right of every row where it
          is always in the same place. */}
      {layouts.map((layout) => (
        <div className="layrow" key={layout.id}>
          <div className="laymain">
            <div className="an">{layout.name || t("Untitled layout")}</div>
            <div className="na-hint">
              {t("{squares} SQ · {pitch} · {waste}% waste", {
                squares: layout.squares ?? "—",
                pitch: layout.average_pitch ?? "—",
                waste: layout.waste_pct ?? 0,
              })}
              {layout.pdf_filename ? ` · ${layout.pdf_filename}` : ""}
            </div>
          </div>
          <button
            type="button"
            className="btn-small danger"
            onClick={() => void onDelete(layout)}
          >
            {t("Remove")}
          </button>
        </div>
      ))}
    </Modal>
  );
}

function ManualLayoutForm({
  job,
  onClose,
  onSave,
}: {
  job: Job;
  onClose: () => void;
  onSave: (input: RoofLayoutInput) => Promise<void>;
}) {
  const fileInput = useRef<HTMLInputElement>(null);

  const [name, setName] = useState("");
  const [pitch, setPitch] = useState("");
  const [waste, setWaste] = useState("");
  const [layers, setLayers] = useState("1");
  const [draft, setDraft] = useState<Draft>(emptyDraft);

  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [filled, setFilled] = useState<string[]>([]);
  const [calculated, setCalculated] = useState<string[]>([]);
  const [reading, setReading] = useState(false);
  const [readError, setReadError] = useState<string | null>(null);
  /*
   * The report is addressed to somebody else.
   *
   * Its own state rather than another `readError`, because it is not an error:
   * the numbers read fine, the form is filled, and everything below is usable.
   * It is the one thing no later check can catch — a report that parses
   * perfectly against the wrong house — so it is said out loud and left to the
   * rep. Jonathon, 2026-08-25: "I want it to just alert us."
   */
  const [nameWarning, setNameWarning] = useState<{
    reported: string;
    customer: string;
  } | null>(null);
  /*
   * The report added the hip and the ridge together.
   *
   * GAF's QuickMeasure prints "Ridge Cap 374 ft", which is both of them, and
   * some reports print nothing else. 374 feet is neither number, so it is
   * never put in either box — see `hip_ridge_lf` in roofReport.ts — and this
   * is what is said instead. Like the name warning above it, it is not an
   * error: everything below is usable, and only the person holding the report
   * can split it.
   */
  const [combinedWarning, setCombinedWarning] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const ready = pitch !== "" && waste !== "" && !busy && !reading;

  async function readPdf(file: File) {
    setReading(true);
    setReadError(null);
    setNameWarning(null);
    setCombinedWarning(null);
    try {
      // The layout goes in as well as the text. Roofr marks its recommended
      // waste percentage by centring a label over the column and shading it,
      // and neither the shading nor the column is anything the text can say.
      // Loaded on demand: pdf.js is too big to ride in the main bundle.
      const { extractPdfReport } = await import("../../lib/roofPdf");
      const report = await extractPdfReport(file);
      const parsed = parseRoofReport(report.text, report.rows);
      setNameWarning(
        reportNameMismatch(report.text, job.contact?.full_name ?? null),
      );
      setPdfFile(file);
      setName((current) => current || file.name.replace(/\.pdf$/i, ""));
      if (parsed.averagePitch && PITCH_OPTIONS.includes(parsed.averagePitch)) {
        setPitch(parsed.averagePitch);
      }
      if (parsed.wastePct !== null) setWaste(String(parsed.wastePct));
      setDraft((current) => {
        const next = { ...current };
        for (const [key, value] of Object.entries(parsed.values)) {
          if (key in next) next[key] = String(value);
        }
        return next;
      });
      setCombinedWarning(combinedHipRidgeNote(parsed));
      setFilled(parsed.matched);
      // Kept apart from the report's own numbers on purpose. A rep checking the
      // form should be able to see which figures Roofr measured and which this
      // company worked out from them.
      setCalculated(parsed.derived);
      if (parsed.matched.length === 0) {
        setReadError(
          t(
            "No measurements found in that PDF. The report is attached — enter the numbers below.",
          ),
        );
      } else if (parsed.wastePct === null && hasWasteTable(report.rows)) {
        // The report offers a choice and the mark on it could not be read.
        // Guessing here means the first column, 10%, which is the number this
        // whole reader exists to stop ordering at — so it asks instead.
        setReadError(
          t(
            "Read the measurements, but could not tell which waste percentage this report recommends — pick it from the list below and check it against the shaded column on the report.",
          ),
        );
      }
    } catch (e) {
      setReadError(
        e instanceof Error
          ? t("Could not read that PDF — {message}", { message: e.message })
          : String(e),
      );
    } finally {
      setReading(false);
    }
  }

  async function submit() {
    if (!ready) return;
    setBusy(true);
    try {
      await onSave({
        name: name.trim() || null,
        average_pitch: pitch,
        waste_pct: Number(waste),
        layers: Math.max(1, Number(layers) || 1),
        source: pdfFile ? "pdf" : "manual",
        pdf_filename: pdfFile?.name ?? null,
        pdfFile,
        values: Object.fromEntries(
          ROOF_LAYOUT_FIELDS.map((f) => [f.key, num(draft[f.key] ?? "")]),
        ),
      });
    } finally {
      setBusy(false);
    }
  }

  const numberField = (key: string, label: string) => (
    <div className="field" key={key}>
      <label>
        {t(label)}
        {filled.includes(key) && (
          <span className="na-hint" style={{ marginLeft: 6 }}>
            {t("from PDF")}
          </span>
        )}
        {calculated.includes(key) && (
          <span className="na-hint" style={{ marginLeft: 6 }}>
            {t("calculated")}
          </span>
        )}
      </label>
      <input
        inputMode="decimal"
        placeholder={t(label)}
        value={draft[key] ?? ""}
        onChange={(e) =>
          setDraft((current) => ({ ...current, [key]: e.target.value }))
        }
      />
    </div>
  );

  // Whatever the report recommends has to be selectable, exactly. Roofr's
  // columns are 10/12/15/16/17/20/22 and this list was built for hand-entry, so
  // a recommendation of 16% had nothing to land on and would have snapped to a
  // neighbour — a different price for the same roof.
  const wasteOptions = [
    ...new Set([
      ...WASTE_PCT_OPTIONS,
      ...(waste !== "" && Number.isFinite(Number(waste))
        ? [Number(waste)]
        : []),
    ]),
  ].sort((a, b) => a - b);

  // Squares sits above the pitch and waste selectors; everything else follows.
  const squaresField = ROOF_LAYOUT_FIELDS[0]!;
  const restFields = ROOF_LAYOUT_FIELDS.slice(1);

  return (
    <Modal
      title={t("Create Manual Roof Layout")}
      subtitle={t(
        "{address} — upload the measurement report or enter the numbers by hand.",
        { address: job.address ?? t("This job") },
      )}
      onClose={onClose}
      actions={
        <>
          <button className="btn-ghost" onClick={onClose}>
            {t("Close")}
          </button>
          <button
            className="btn-primary"
            disabled={!ready}
            style={ready ? undefined : { opacity: 0.5, cursor: "not-allowed" }}
            onClick={() => void submit()}
          >
            {busy ? t("Saving…") : t("Submit")}
          </button>
        </>
      }
    >
      <div className="uploadrow">
        <input
          ref={fileInput}
          type="file"
          accept="application/pdf,.pdf"
          style={{ display: "none" }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void readPdf(file);
            e.target.value = "";
          }}
        />
        <button
          className="btn-ghost"
          disabled={reading}
          onClick={() => fileInput.current?.click()}
        >
          {reading ? t("Reading…") : t("Upload PDF report")}
        </button>
        <span className="na-hint">
          {pdfFile
            ? `${pdfFile.name} — ${tn(
                filled.length,
                "1 measurement read. Check them before submitting.",
                "{n} measurements read. Check them before submitting.",
              )}`
            : t("Roofr, EagleView, iRoofing or Hover. Optional.")}
        </span>
      </div>
      {readError && (
        <div className="err" style={{ margin: "8px 0" }}>
          {readError}
        </div>
      )}
      {nameWarning && (
        // A warning, not a refusal. Reports are legitimately addressed to a
        // spouse, a property manager or an adjuster — the rep knows which, and
        // nobody else can.
        <div className="config-warn" role="alert" style={{ margin: "8px 0" }}>
          <strong>{t("Check this is the right report.")}</strong>{" "}
          {t("It is made out to")} <b>{nameWarning.reported}</b>,{" "}
          {t("and this job is")} <b>{nameWarning.customer}</b>.{" "}
          {t(
            "Carry on if that is expected — a report is often in a spouse’s or an adjuster’s name.",
          )}
        </div>
      )}

      {combinedWarning && (
        <div className="config-warn" role="alert" style={{ margin: "8px 0" }}>
          <strong>{t("Hip and ridge came as one number.")}</strong>{" "}
          {combinedWarning}
        </div>
      )}

      <div className="fgrid one">
        <div className="field">
          <label>{t("Name")}</label>
          <input
            placeholder={t("Name")}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        {numberField(squaresField.key, squaresField.label)}

        <div className="field">
          <label>
            {t("Average Pitch")} <span className="req">*</span>
          </label>
          <select value={pitch} onChange={(e) => setPitch(e.target.value)}>
            <option value="">{t("Average Pitch")}</option>
            {PITCH_OPTIONS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label>
            {t("Waste pct")} <span className="req">*</span>
          </label>
          <select value={waste} onChange={(e) => setWaste(e.target.value)}>
            <option value="">{t("Waste pct")}</option>
            {wasteOptions.map((w) => (
              <option key={w} value={w}>
                {w}%
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label>{t("Layers to tear off")}</label>
          <select value={layers} onChange={(e) => setLayers(e.target.value)}>
            {[1, 2, 3, 4].map((n) => (
              <option key={n} value={n}>
                {tn(n, "1 layer", "{n} layers")}
              </option>
            ))}
          </select>
          <span className="na-hint">
            {t(
              "Two layers is twice the tear-off, and can mean a second dump trailer.",
            )}
          </span>
        </div>

        {restFields.map((f) => numberField(f.key, f.label))}
      </div>
    </Modal>
  );
}
